/**
 * /Recon portal auth pages — login / setup / forgot / reset.
 *
 * All four share the same centered "auth card" layout and submit a
 * single form. The forms call into the ClientAuthContext functions and
 * surface errors as a banner above the inputs. No client-side
 * validation gymnastics; the API enforces password rules and we render
 * whatever it tells us went wrong.
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useClientAuth } from "@/auth/client-auth-context";
import {
  forgotPasswordApiV1ClientAuthForgotPost,
  verifyEmailApiV1ClientAuthVerifyEmailVerifyTokenPost,
} from "@hackereyes/api-client";
import "./auth-pages.css";

/* ============================== Login ============================== */

export function LoginPage() {
  const { login, isAuthenticated } = useClientAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Auto-redirect already-authenticated users straight to the portal.
  // Without this, returning from Stripe's cancel flow (which links the
  // user to / which redirects to /portal/login) shows the sign-in form
  // to a user who still has a valid refresh cookie — confusing UX that
  // implies they've been signed out when they haven't.
  useEffect(() => {
    if (isAuthenticated) {
      const intended = (location.state as { from?: string } | null)?.from;
      navigate(intended || "/portal", { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      const intended = (location.state as { from?: string } | null)?.from;
      navigate(intended || "/portal", { replace: true });
    } catch (err) {
      setError(humanizeAuthError((err as Error).message));
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Sign in to /Recon"
      lede="See your engagement details, deliverables, and remediation tracking."
    >
      {error && <AuthBanner kind="error">{error}</AuthBanner>}
      <form className="auth-form" onSubmit={onSubmit}>
        <AuthField label="Email" htmlFor="email">
          <input
            id="email"
            type="email"
            className="auth-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            autoFocus
          />
        </AuthField>
        <AuthField label="Password" htmlFor="password">
          <input
            id="password"
            type="password"
            className="auth-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </AuthField>
        <button type="submit" className="recon-btn recon-btn-primary auth-submit" disabled={submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </button>
        <div className="auth-form-meta">
          <Link to="/portal/forgot">Forgot your password?</Link>
        </div>
      </form>

      {/* Self-serve signup CTA — prominent secondary path. Visitors who
          arrive from the marketing site without an existing account
          need a path to "commission a Quick Check" that's at least as
          visible as the sign-in form. */}
      <div className="auth-signup-cta">
        <div className="auth-signup-cta-divider"><span>or</span></div>
        <p className="auth-signup-cta-headline">New to /Recon?</p>
        <p className="auth-signup-cta-body">
          Create an account to commission a /Recon engagement. $249 base.
          No card required to sign up.
        </p>
        <Link to="/portal/signup" className="recon-btn recon-btn-secondary auth-signup-cta-btn">
          Create an account →
        </Link>
      </div>
    </AuthCard>
  );
}

/* ============================== Signup ============================== */

export function SignupPage() {
  const { signup } = useClientAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await signup(email, password, companyName || undefined);
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(humanizeAuthError((err as Error).message));
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Create your account"
      lede="Create an account to commission a /Recon assessment."
    >
      <p className="auth-explain">
        Verify your email after signup, then commission your first
        assessment from inside the portal. No payment until you commit
        to an engagement.
      </p>
      {error && <AuthBanner kind="error">{error}</AuthBanner>}
      <form className="auth-form" onSubmit={onSubmit}>
        <AuthField label="Email" htmlFor="signup-email">
          <input
            id="signup-email"
            type="email"
            className="auth-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            autoFocus
          />
        </AuthField>
        <AuthField label="Password" htmlFor="signup-password">
          <input
            id="signup-password"
            type="password"
            className="auth-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
          />
          <p className="auth-hint">12 characters minimum.</p>
        </AuthField>
        <AuthField label="Company (optional)" htmlFor="signup-company">
          <input
            id="signup-company"
            type="text"
            className="auth-input"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            autoComplete="organization"
            maxLength={255}
          />
          <p className="auth-hint">For business assessments. Leave blank for personal.</p>
        </AuthField>
        <button type="submit" className="recon-btn recon-btn-primary auth-submit" disabled={submitting}>
          {submitting ? "Creating account…" : "Create account"}
        </button>
      </form>
      <div className="auth-footer-links">
        <Link to="/portal/login">Already have an account? Sign in →</Link>
      </div>
    </AuthCard>
  );
}

/* ============================== Verify email ============================== */

export function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const { refreshMe } = useClientAuth();
  const [status, setStatus] = useState<"verifying" | "success" | "error">("verifying");
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        setStatus("error");
        setErrorCode("verify_token_invalid");
        return;
      }
      try {
        const { error } = await verifyEmailApiV1ClientAuthVerifyEmailVerifyTokenPost({
          path: { verify_token: token },
        });
        if (cancelled) return;
        if (error) {
          const detail = (error as Record<string, unknown>)?.detail as string | undefined;
          setErrorCode(detail || "verify_token_invalid");
          setStatus("error");
        } else {
          setStatus("success");
          // Re-fetch /me so the in-memory profile reflects the new
          // email_verified=true. Without this, the user has to log out
          // and back in for the commission gate to unlock.
          await refreshMe();
        }
      } catch (err) {
        if (cancelled) return;
        setErrorCode((err as Error).message || "verify_token_invalid");
        setStatus("error");
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, refreshMe]);

  if (status === "verifying") {
    return (
      <AuthCard title="Verifying your email…" lede="One moment.">
        <p className="auth-card-lede">If this takes more than a few seconds, refresh the page.</p>
      </AuthCard>
    );
  }

  if (status === "success") {
    return (
      <AuthCard
        title="Email verified"
        lede="Your account is fully active. Commission your first assessment whenever you're ready."
      >
        <div className="auth-footer-links">
          <Link to="/portal" className="recon-btn recon-btn-primary auth-submit">
            Continue to portal →
          </Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Verification link is no longer valid"
      lede={humanizeAuthError(errorCode || "verify_token_invalid")}
    >
      <div className="auth-footer-links">
        <Link to="/portal/login">Sign in →</Link>
      </div>
    </AuthCard>
  );
}

/* ============================== Setup ============================== */

export function SetupPage() {
  const { setupToken } = useParams<{ setupToken: string }>();
  const { consumeSetupToken } = useClientAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don’t match.");
      return;
    }
    setSubmitting(true);
    try {
      await consumeSetupToken(setupToken!, password);
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(humanizeAuthError((err as Error).message));
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Set your password"
      lede="Welcome to /Recon. Choose a password to access your engagement and download deliverables."
    >
      {error && <AuthBanner kind="error">{error}</AuthBanner>}
      <form className="auth-form" onSubmit={onSubmit}>
        <AuthField label="New password" htmlFor="password">
          <input
            id="password"
            type="password"
            className="auth-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            autoFocus
          />
          <p className="auth-hint">At least 8 characters.</p>
        </AuthField>
        <AuthField label="Confirm password" htmlFor="confirm">
          <input
            id="confirm"
            type="password"
            className="auth-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </AuthField>
        <button type="submit" className="recon-btn recon-btn-primary auth-submit" disabled={submitting}>
          {submitting ? "Setting password…" : "Set password and sign in"}
        </button>
      </form>
    </AuthCard>
  );
}

/* ============================== Forgot ============================== */

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Always returns 204; we don't reveal whether the email matched
      await forgotPasswordApiV1ClientAuthForgotPost({
        body: { email } as never,
      });
    } catch {
      // Same UI either way — don't enumerate
    }
    setSubmitting(false);
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <AuthCard
        title="Check your email"
        lede="If an account exists for that address, we’ve sent a link to reset your password. The link is valid for 2 hours."
      >
        <div className="auth-footer-links">
          <Link to="/portal/login">Back to sign in</Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Reset your password"
      lede="Enter your email and we’ll send you a link to choose a new password."
    >
      <form className="auth-form" onSubmit={onSubmit}>
        <AuthField label="Email" htmlFor="email">
          <input
            id="email"
            type="email"
            className="auth-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            autoFocus
          />
        </AuthField>
        <button type="submit" className="recon-btn recon-btn-primary auth-submit" disabled={submitting}>
          {submitting ? "Sending…" : "Send reset link"}
        </button>
      </form>
      <div className="auth-footer-links">
        <Link to="/portal/login">Back to sign in</Link>
      </div>
    </AuthCard>
  );
}

/* ============================== Reset ============================== */

export function ResetPage() {
  const { resetToken } = useParams<{ resetToken: string }>();
  const { consumeResetToken } = useClientAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don’t match.");
      return;
    }
    setSubmitting(true);
    try {
      await consumeResetToken(resetToken!, password);
      navigate("/portal/login", {
        replace: true,
        state: { resetSuccess: true },
      });
    } catch (err) {
      setError(humanizeAuthError((err as Error).message));
      setSubmitting(false);
    }
  }

  return (
    <AuthCard title="Choose a new password" lede="Pick something you don’t use elsewhere.">
      {error && <AuthBanner kind="error">{error}</AuthBanner>}
      <form className="auth-form" onSubmit={onSubmit}>
        <AuthField label="New password" htmlFor="password">
          <input
            id="password"
            type="password"
            className="auth-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            autoFocus
          />
        </AuthField>
        <AuthField label="Confirm password" htmlFor="confirm">
          <input
            id="confirm"
            type="password"
            className="auth-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </AuthField>
        <button type="submit" className="recon-btn recon-btn-primary auth-submit" disabled={submitting}>
          {submitting ? "Saving…" : "Set new password"}
        </button>
      </form>
    </AuthCard>
  );
}

/* ============================== Primitives ============================== */

function AuthCard({
  title, lede, children,
}: { title: string; lede?: string; children: React.ReactNode }) {
  return (
    <div className="auth-page">
      <div className="auth-page-inner">
        {/* Visible escape hatch — a visitor who landed on an auth page
            without an account (e.g. clicked a marketing CTA expecting
            content) needs a way out that's NOT a sign-in form. Sends
            them to the marketing site's product page. The brand lockup
            inside the card also navigates, but a clearly-labeled
            top-left back-link is the standard escape pattern.

            href, not <Link to>: this is an external navigation to
            signumcyber.com, not a same-SPA route. */}
        <a href="https://signumcyber.com/recon" className="auth-page-back">
          ← Back to signumcyber.com
        </a>
        <div className="auth-card">
          <Link to="/portal/login" className="auth-card-brand" aria-label="Recon home">
            <img src="/recon_flame.svg" alt="" className="auth-card-flame" aria-hidden="true" />
            <span className="recon-brand-lockup">
              <img
                src="/wordmark_signumcyber.png"
                alt="SignumCyber"
                className="recon-brand-wordmark-img"
              />
              <span className="recon-brand-recon">/recon</span>
            </span>
          </Link>
          <h1 className="auth-card-title">{title}</h1>
          {lede && <p className="auth-card-lede">{lede}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

function AuthField({
  label, htmlFor, children,
}: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className="auth-field">
      <label htmlFor={htmlFor} className="auth-label">{label}</label>
      {children}
    </div>
  );
}

function AuthBanner({
  kind, children,
}: { kind: "error" | "info"; children: React.ReactNode }) {
  return <div className={`auth-banner auth-banner-${kind}`}>{children}</div>;
}

const ERROR_MESSAGES: Record<string, string> = {
  credentials_invalid: "Email or password is incorrect.",
  account_locked: "Your account is temporarily locked after too many failed attempts. Try again in a few minutes.",
  password_not_set: "You haven’t set a password yet. Check your email for the setup link.",
  password_too_short: "Password must be at least 12 characters.",
  setup_token_invalid: "This setup link has expired or already been used.",
  reset_token_invalid: "This reset link has expired or already been used.",
  verify_token_invalid: "This verification link has expired or already been used.",
  email_already_registered: "An account with this email already exists. Sign in instead.",
  token_invalid: "Your session has expired. Please sign in again.",
  token_wrong_type: "Your session is invalid. Please sign in again.",
};

function humanizeAuthError(code: string): string {
  return ERROR_MESSAGES[code] || `Couldn’t complete that. (${code})`;
}
